import { useEffect, useState } from 'react';

import { skillStoreVisibilityService } from '../services/skillStoreVisibilityService';

/**
 * Resolve the deployment-wide Skill Store visibility flag.
 *
 * The first paint uses the value this session (or the previous visit) already
 * resolved, so a deployment that turned the Skill Store off does not flash the
 * link, the skill builder, or the cashout entry on repeat visits. With nothing
 * known yet — a first-ever visit, or a failed read — the surfaces show, matching
 * the server-side default, so a network hiccup never strips working features.
 */
export const useSkillStoreVisibility = (): boolean => {
  const [visible, setVisible] = useState<boolean>(
    () => skillStoreVisibilityService.peekSkillStoreVisibility() ?? true,
  );

  useEffect(() => {
    let active = true;

    const unsubscribe = skillStoreVisibilityService.subscribeSkillStoreVisibility((enabled) => {
      if (active) setVisible(enabled);
    });

    skillStoreVisibilityService.getSkillStoreVisibility()
      .then((settings) => { if (active) setVisible(settings.enabled); })
      .catch(() => { if (active) setVisible(true); });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  return visible;
};
